/**
 * AIServiceContext.jsx
 * 
 * Centralized AI Service context for managing AI integration state across the application.
 * This provides a single source of truth for AI enabled status and configuration.
 * 
 * IMPORTANT ARCHITECTURE NOTE:
 * ============================
 * The frontend NEVER communicates directly with any AI service (Gemini, DGX Spark, Ollama, etc.).
 * All AI communication flows through the backend API:
 * 
 *   Frontend ? Backend API ? AI Service
 *   
 *   1. Frontend calls /api/insights/enabled (backend endpoint)
 *   2. Backend checks database for enabled status
 *   3. If enabled, frontend calls /api/insights/* endpoints
 *   4. Backend (InsightsController) ? IInsightsProvider ? AI Service
 *   5. Backend returns processed AI response to frontend
 * 
 * This ensures:
 * - Security: AI API keys never exposed to frontend
 * - Control: Backend controls all AI communication
 * - Flexibility: Easy to swap AI providers without frontend changes
 * - Consistency: Single point of control for AI features
 * 
 * Architecture Goals:
 * - Single enabled check on context initialization (prevents multiple API calls per page)
 * - Easy provider swapping on backend (Gemini -> DGX Spark -> Ollama -> Custom)
 * - Supports multiple AI capabilities: insights, document analysis, summarization, etc.
 * - Future-proof for local LLM hosting (DGX Spark in same environment as API)
 * 
 * Usage:
 * 1. Wrap your app with <AIServiceProvider> (typically in App.jsx)
 * 2. Use the useAIService() hook in components that need AI features
 * 3. Check aiEnabled before making AI-related API calls to YOUR BACKEND
 * 
 * Example:
 * ```jsx
 * const { aiEnabled, isLoading } = useAIService();
 * 
 * // Only call backend AI endpoints if enabled
 * if (!aiEnabled) return null;
 * 
 * // This calls YOUR backend, not the AI service directly
 * const response = await apiFetch('/api/insights/it-executive-summary');
 * ```
 * 
 * Future DGX Spark Migration:
 * When migrating to DGX Spark (local LLM in same environment as API):
 * 1. Create new backend provider: DgxSparkInsightsProvider.cs
 * 2. Register in Program.cs DI container
 * 3. Update Insights:Provider config to 'dgx-spark'
 * 4. No frontend changes needed - backend handles all AI communication
 */

import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { useAuth } from './AuthContext';

// Default configuration for when backend returns no config
const DEFAULT_AI_CONFIG = {
    provider: 'none', // 'gemini', 'ollama', 'dgx-spark', 'none'
    enabled: false,
    features: {
        insights: false,      // IT Report summaries, executive summaries
        documentAnalysis: false, // Future: reading legal documents
        summarization: false,    // Future: action item summaries
        riskAnalysis: false,     // Risk register generation
    }
};

// Create the context
const AIServiceContext = createContext({
    aiEnabled: false,
    isLoading: true,
    config: DEFAULT_AI_CONFIG,
    error: null,
    refreshConfig: () => Promise.resolve(),
});

/**
 * Hook to access AI service state
 * @returns {{ aiEnabled: boolean, isLoading: boolean, config: object, error: string|null, refreshConfig: Function }}
 */
export const useAIService = () => useContext(AIServiceContext);

/**
 * AI Service Provider Component
 * Wraps the application to provide centralized AI state management
 */
export const AIServiceProvider = ({ children }) => {
    const { user } = useAuth();
    const [aiEnabled, setAiEnabled] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [config, setConfig] = useState(DEFAULT_AI_CONFIG);
    const [error, setError] = useState(null);

    /**
     * Fetch AI configuration from the backend.
     * Only called when the user is authenticated.
     */
    const fetchAIConfig = useCallback(async () => {
        setIsLoading(true);
        setError(null);

        try {
            const response = await fetch('/api/insights/enabled');

            if (!response.ok) {
                setAiEnabled(false);
                setConfig(DEFAULT_AI_CONFIG);
                return;
            }

            const data = await response.json();
            const enabled = !!data.enabled;

            setAiEnabled(enabled);
            setConfig({
                ...DEFAULT_AI_CONFIG,
                provider: data.provider || (enabled ? 'gemini' : 'none'),
                enabled,
                features: {
                    insights: enabled,
                    documentAnalysis: enabled,
                    summarization: enabled,
                    riskAnalysis: enabled,
                }
            });
        } catch (err) {
            Sentry.captureException(err);
            setError('Failed to load AI configuration');
            setAiEnabled(false);
            setConfig(DEFAULT_AI_CONFIG);
        } finally {
            setIsLoading(false);
        }
    }, []);

    /**
     * Allow manual refresh of AI configuration.
     * Useful when settings are changed in the admin panel.
     */
    const refreshConfig = useCallback(async () => {
        await fetchAIConfig();
    }, [fetchAIConfig]);

    // Only fetch AI config when the user is authenticated.
    // Unauthenticated pages (login, password reset, etc.) must never trigger this.
    useEffect(() => {
        (async () => {
            if (!user) {
                setAiEnabled(false);
                setConfig(DEFAULT_AI_CONFIG);
                setIsLoading(false);
                return;
            }
            await fetchAIConfig();
        })();
    }, [user, fetchAIConfig]);

    // Provide context value to children
    const contextValue = {
        aiEnabled,
        isLoading,
        config,
        error,
        refreshConfig,
    };

    return (
        <AIServiceContext.Provider value={contextValue}>
            {children}
        </AIServiceContext.Provider>
    );
};

export default AIServiceContext;
